import React, { useState, useEffect, useRef } from 'react';
import { BACKEND_URL, useOasisBackend } from '../../hooks/useOasisBackend';
import SettingsButton from '../components/SettingsButton';
import SearchBar from '../components/SearchBar';
import SearchResultCard from '../components/SearchResultCard';
import WebSearchResultCard from '../components/WebSearchResultCard';
import LoadingCard from '../components/LoadingCard';
import type { WebResult, ActionResult, ChatMessage } from '../../api/OasisBackendApi';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ChatWindow from '../components/ChatWindow';
import { useUser } from '../../hooks/useUser';
import { LoginItem } from '../../components/Login';
import { SignUpItem } from '../../components/SignupButton';
import { LogoutItem } from '../../components/LogoutButton';
import { useLoggedOutUserSearchCounter } from '../hooks/useLoggedOutUserSearchCounter';
import ReconnectingWebSocket from 'reconnecting-websocket';

interface WebSocketMessage {
  type: 'action_result' | 'external_search' | 'chat';
  data: ActionResult[] | WebResult[] | any;
}

const Homepage: React.FC = () => {
  const db = useOasisBackend();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isActionsLoading, setIsActionsLoading] = useState(false);
  const [isWebLoading, setIsWebLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<ActionResult[]>([]);
  const [webResults, setWebResults] = useState<WebResult[]>([]);
  const [chatResetKey, setChatResetKey] = useState(0);
  const [chatInitialMessage, setChatInitialMessage] = useState<string | undefined>(undefined);
  const [availableDomains, setAvailableDomains] = useState<string[]>([]);
  const user = useUser();
  const loggedIn = user.state === "LoggedIn";
  const { incrementSearchCount, LimitReachedMessage, hasReachedLimit, showLimitReached, setShowLimitReached, isInitialized } = useLoggedOutUserSearchCounter(loggedIn);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [accumulatedText, setAccumulatedText] = useState('');

  // Helper function to track user interactions
  const trackInteraction = async (type: 'action' | 'web' | 'google_it' | 'chat', actionId?: number) => {
    if (!initialQuery) return;
    
    // Use setTimeout to ensure this runs asynchronously and doesn't block the redirect
    setTimeout(async () => {
      try {
        await db.endpoints.rhea.rheaApiAddQueryResultMatchCreate({
          query: initialQuery,
          type: type,
          action: actionId || 0, // Use 0 as default for non-action types
        } as any); // Use 'as any' to bypass the type check since we're only sending creation fields
      } catch (error) {
        console.error('Failed to track interaction:', error);
        // Silently fail - tracking shouldn't affect user experience
      }
    }, 0);
  };

  // Custom scrollbar styles
  const scrollbarStyles = `
    /* For Webkit browsers (Chrome, Safari) */
    ::-webkit-scrollbar {
      display: none;
    }

    /* For Firefox */
    * {
      scrollbar-width: none;
    }
  `;

  // Get initial query from URL
  const initialQuery = searchParams.get('q') || '';

  const ws = useRef<ReconnectingWebSocket | null>(null);

  // Establish websocket connection
  useEffect(() => {
    // Remove any existing protocol and add wss://
    let prefix = 'wss://';
    if (BACKEND_URL.includes('127.0.0.1')) {
      prefix = 'ws://';
    }
    const wsUrl = `${prefix}${BACKEND_URL.replace(/^https?:\/\//, '')}/ws/rhea/`;
    
    ws.current = new ReconnectingWebSocket(wsUrl);
    
    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data) as WebSocketMessage;
      console.log("WebSocket message received:", data);

      if (data.type === 'action_result') {
        const mappedResults: ActionResult[] = (data.data as ActionResult[]).map((result) => ({
          title: result.title || '',
          description: result.description || '',
          url: result.url || '#',
          image_url: result.image_url
        }));
        setSearchResults(mappedResults);
        setIsActionsLoading(false);
      } else if (data.type === 'external_search') {
        setWebResults(data.data as WebResult[]);
        setIsWebLoading(false);
      } else if (data.type === 'chat') {
        if (Array.isArray(data.data)) {
          setMessages(data.data as ChatMessage[]);
        } else {
          const chatData = data.data as { 
            conversation_id?: number; 
            chunk?: string;
            done?: boolean;
            error?: string;
          };
          
          if (chatData.conversation_id && !conversationId) {
            setConversationId(chatData.conversation_id);
          }
          
          if (chatData.error) {
            setMessages(prev => [...prev, { 
              role: 'system', 
              content: `Error: ${chatData.error}` 
            }]);
            return;
          }
          
          if (chatData.done) {
            return;
          }
          
          if (chatData.chunk) {
            console.log('Received chunk:', chatData.chunk);
            setMessages((prev: ChatMessage[]) => {
              console.log('Previous messages:', prev);
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.role === 'assistant') {
                const updatedMessage = { 
                  ...lastMessage, 
                  content: lastMessage.content + (chatData.chunk as string) 
                };
                console.log('Updated message:', updatedMessage);
                return [
                  ...newMessages.slice(0, -1),
                  updatedMessage
                ];
              } else {
                const newMessage = { role: 'assistant', content: chatData.chunk as string };
                console.log('New message:', newMessage);
                return [...newMessages, newMessage];
              }
            });
          }
        }
      }
    };

    ws.current.onopen = () => {
      console.log("WebSocket connection established");
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.current.onclose = () => {
      console.log("WebSocket connection closed");
    };

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []);

  // Fetch available domains on mount
  useEffect(() => {
    db.endpoints.rhea.rheaApiPublicDomainsRetrieve()
      .then((response) => {
        if (Array.isArray(response.data)) {
          setAvailableDomains(response.data);
        } else {
          setAvailableDomains([]);
        }
      })
      .catch(() => {
        setAvailableDomains([]);
      });
  }, []);

  // Perform search if there's an initial query
  useEffect(() => {
    if (initialQuery && isInitialized) {
      if (!loggedIn && hasReachedLimit) {
        setShowLimitReached(true);
        return;
      }
      handleSearch(initialQuery);
    }
  }, [initialQuery, isInitialized]);

  const handleSearch = async (query: string) => {
    if (!loggedIn && hasReachedLimit) {
      setShowLimitReached(true);
      return;
    }

    if (query) {
      navigate(`?q=${encodeURIComponent(query)}`);
    } else {
      navigate('/');
    }

    if (!loggedIn) {
      incrementSearchCount();
    }

    setIsLoading(true);
    setIsActionsLoading(true);
    setIsWebLoading(true);
    setSearchResults([]);
    setWebResults([]);
    setAccumulatedText('');

    try {
      db.endpoints.rhea.rheaApiSimpleSearchCreate({ q: query })
        .then(() => {
          // Results will come through WebSocket
        })
        .catch(() => {
          setSearchResults([]);
          setIsActionsLoading(false);
        });

      db.endpoints.rhea.rheaApiExternalSearchCreate({ q: query })
        .then(() => {
          // Results will come through WebSocket
        })
        .catch(() => {
          setWebResults([]);
          setIsWebLoading(false);
        });

      setChatInitialMessage(query);
      setChatResetKey(prev => prev + 1);
    } catch (error) {
      setSearchResults([]);
      setWebResults([]);
      setChatInitialMessage(undefined);
      setChatResetKey(prev => prev + 1);
      setIsActionsLoading(false);
      setIsWebLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDomainSelect = (selectedDomain: string) => {
    if (selectedDomain === 'all') {
      navigate(`?q=${encodeURIComponent(initialQuery)}`);
    } else {
      navigate(`/agentic?agent=rhea_domain&q=${encodeURIComponent(initialQuery)}&domain=${encodeURIComponent(selectedDomain)}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--rhea-bg-primary)', color: 'var(--rhea-text-primary)' }}>
      <style>{scrollbarStyles}</style>
      <LimitReachedMessage />
      {initialQuery || isLoading || searchResults.length > 0 || webResults.length > 0 ? (
        <>
          <div className="sticky top-0 z-10 bg-inherit border-b w-full" style={{ borderColor: 'var(--rhea-card-border)' }}>
            <div className="px-4 py-4 flex flex-col md:flex-row md:justify-between relative">
              <div className="flex flex-col md:flex-row items-start md:items-start gap-4 md:gap-8 w-full">
                <div className="flex items-center gap-4 cursor-pointer shrink-0 mb-2 md:mb-0" onClick={() => window.location.href = '/'}>
                  <img src="/assets/rhea/logo.png" alt="Rhea" className="h-16" />
                  <span className="text-3xl font-semibold" style={{ color: 'var(--rhea-text-primary)' }}>Rhea</span>
                </div>
                <div className="flex-1 flex flex-col gap-2 min-w-0 w-full">
                  <SearchBar 
                    onSearch={handleSearch} 
                    isLoading={isLoading} 
                    initialValue={initialQuery}
                    autoFocus={true}
                    key="top-search"
                  />
                  {availableDomains.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-2 w-full justify-center">
                      <button
                        onClick={() => handleDomainSelect('all')}
                        className={`px-3 py-1 rounded-lg text-sm whitespace-nowrap border ${
                          !searchParams.get('domain') 
                            ? 'border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)]' 
                            : 'border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]'
                        }`}
                      >
                        All
                      </button>
                      {availableDomains.map((domainName) => (
                        <button
                          key={domainName}
                          onClick={() => handleDomainSelect(domainName)}
                          className={`px-3 py-1 rounded-lg text-sm whitespace-nowrap border ${
                            searchParams.get('domain') === domainName 
                              ? 'border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)]' 
                              : 'border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]'
                          }`}
                        >
                          {domainName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Mobile: absolute top right, Desktop: normal */}
              <div className="hidden md:flex items-start gap-2 shrink-0">
                {!loggedIn ? (
                  <>
                    <LoginItem title="Login to ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
                    <SignUpItem title="Sign up for ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)] hover:bg-[var(--rhea-card-hover)]"/>
                  </>
                ) : (
                  <>
                    <SettingsButton />
                    <LogoutItem className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
                  </>
                )}
              </div>
              <div className="flex md:hidden items-center gap-2 absolute right-4 top-4 z-20">
                {!loggedIn ? (
                  <>=
                    <LoginItem title="Login to ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
                    <SignUpItem title="Sign up for ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)] hover:bg-[var(--rhea-card-hover)]"/>
                  </>
                ) : (
                  <>
                    <SettingsButton />
                    <LogoutItem className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 container mx-auto px-4 py-8">
            {/* Chat Window */}
            {chatResetKey > 0 ? (
              <div className="max-w-full mb-8 transition-all duration-200 hover:scale-[1.02] hover:shadow-xl">
                <ChatWindow 
                  initialMessage={chatInitialMessage} 
                  resetKey={chatResetKey} 
                  messages={messages}
                  setMessages={setMessages}
                  conversationId={conversationId}
                  setConversationId={setConversationId}
                  onFirstInteraction={() => trackInteraction('chat')}
                />
              </div>
            ) : <></>}

            {/* Display local actions in a single row */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--rhea-text-primary)' }}>Available Actions</h2>
              <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory">
                {/* Just Google It tile - only show when there's a query */}
                {initialQuery && (
                  <div className="snap-center shrink-0 p-4">
                    <div 
                      onClick={() => {
                        trackInteraction('google_it');
                        window.open(`https://google.com/search?q=${encodeURIComponent(initialQuery)}`, '_blank');
                      }}
                      className="w-32 h-32 bg-[var(--rhea-card-bg)] border border-[var(--rhea-card-border)] rounded-lg p-4 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02] flex flex-col items-center justify-center gap-2 shadow-md"
                      style={{ 
                        backgroundColor: 'var(--rhea-card-bg)',
                        border: '1px solid var(--rhea-card-border)'
                      }}
                    >
                      <img 
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/800px-Google_%22G%22_logo.svg.png" 
                        alt="Google" 
                        className="h-8 w-8"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">G</div>
                      <span className="text-[var(--rhea-text-primary)] font-medium text-center">Just Google It</span>
                    </div>
                  </div>
                )}
                {isActionsLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="snap-center shrink-0">
                        <LoadingCard type="action" />
                      </div>
                    ))
                  : searchResults.map((result, index) => (
                      <div key={index} className="snap-center shrink-0 p-4">
                        <SearchResultCard
                          title={result.title}
                          description={result.description}
                          url={result.url}
                          image_url={result.image_url}
                          onTrack={() => trackInteraction('action', index + 1)}
                        />
                      </div>
                    ))}
              </div>
            </div>

            {/* Display web search results */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--rhea-text-primary)' }}>Web References</h2>
              <div className="grid gap-4">
                {isWebLoading
                  ? Array.from({ length: 4 }).map((_, i) => <LoadingCard key={i} type="web" />)
                  : webResults.map((result) => (
                      <WebSearchResultCard 
                        key={result.id} 
                        result={result} 
                        onTrack={() => trackInteraction('web')}
                      />
                    ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        // Show centered search when no content (like Google homepage)
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="text-center mb-8 w-full px-4">
            <div className="flex items-center justify-center gap-2 mb-8">
              <img src="/assets/rhea/logo.png" alt="Rhea" className="h-16" />
              <span className="text-5xl font-semibold" style={{ color: 'var(--rhea-text-primary)' }}>Rhea</span>
            </div>
            <div className="w-full max-w-6xl mx-auto">
              <SearchBar 
                onSearch={handleSearch} 
                isLoading={isLoading} 
                initialValue={initialQuery}
                autoFocus={true}
                key="centered-search"
              />
              {availableDomains.length > 0 && (
                <div className="flex gap-2 mt-2 justify-center overflow-x-auto pb-2">
                  <button
                    onClick={() => handleDomainSelect('all')}
                    className={`px-3 py-1 rounded-lg text-sm whitespace-nowrap border ${
                      !searchParams.get('domain') 
                        ? 'border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)]' 
                        : 'border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]'
                    }`}
                  >
                    All
                  </button>
                  {availableDomains.map((domainName) => (
                    <button
                      key={domainName}
                      onClick={() => handleDomainSelect(domainName)}
                      className={`px-3 py-1 rounded-lg text-sm whitespace-nowrap border ${
                        searchParams.get('domain') === domainName 
                          ? 'border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)]' 
                          : 'border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]'
                      }`}
                    >
                      {domainName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="absolute top-4 right-4 flex items-center gap-2">
            {!loggedIn ? (
              <>
                <LoginItem title="Login to ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
                <SignUpItem title="Sign up for ThoughtfulOasis" className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-accent-primary)] text-[var(--rhea-accent-primary)] hover:bg-[var(--rhea-card-hover)]"/>
              </>
            ) : (
              <>
                <SettingsButton />
                <LogoutItem className="px-3 py-1 rounded-lg text-sm whitespace-nowrap border border-[var(--rhea-card-border)] text-[var(--rhea-text-secondary)] hover:bg-[var(--rhea-card-hover)]"/>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Homepage;
